import React from "react";
import { Link } from "react-router-dom";

// Material UI components
import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Drawer from "@material-ui/core/Drawer";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import ArrowDropDownSharpIcon from "@material-ui/icons/ArrowDropDownSharp";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import MenuIcon from "@material-ui/icons/Menu";

// Local
import About from "../components/about";
import { LogoutMenuItem } from "../authentication/logout";
import Expanded from "./expanded";
import DrawerNavigation from "./drawerNavigation";
import DrawerProfile from "./drawerProfile";
import Profile from "./profile";
import useStyles from "./styles";
import routes from "../routes";
import { menuItemIconMargin } from "../globalStyles";
import { localStorageKey } from "../constants";
import { useUserId } from "../authentication/hooks";

interface Props {
  withLinks?: boolean;
}
const Navigation: React.FC<Props> = ({ withLinks = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [aboutOpen, setAboutOpen] = React.useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const userId = useUserId();

  const handleMenu = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDrawerToggle = React.useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen]);

  const handleDrawerClose = React.useCallback(() => {
    setDrawerOpen(false);
  }, []);

  return (
    <>
      <AppBar elevation={2} position="static" component="nav" className={classes.appBar}>
        <Toolbar className={classes.toolbar} variant="dense">
          <Button
            component={Link}
            to={routes.workspacesList}
            onClick={() => localStorage.removeItem(localStorageKey.lastUsedWorkspace(userId))}
          >
            <img src="/logo.svg" alt="Listiq" className={classes.logo} />
          </Button>
          <div
            className={classes.toolbarRight}
            style={{
              justifyContent: withLinks ? (isMobile ? "flex-end" : "space-between") : "flex-end",
            }}
          >
            {withLinks && !isMobile ? <Expanded /> : null}
            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                className={classes.menuButton}
              >
                <MenuIcon />
              </IconButton>
            ) : null}
            {!isMobile && (
              <IconButton
                className={classes.profileButton}
                data-cy="menu-user"
                onClick={handleMenu}
                color="inherit"
              >
                <Profile />
                <ArrowDropDownSharpIcon style={{ marginLeft: "0.5em" }} />
              </IconButton>
            )}
          </div>
        </Toolbar>
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={!!anchorEl}
          onClose={handleClose}
        >
          <MenuItem onClick={() => setAboutOpen(true)}>
            <InfoOutlinedIcon style={menuItemIconMargin} />
            About
          </MenuItem>
          <LogoutMenuItem />
        </Menu>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerContent}>
          <div className={classes.drawerProfile}>
            <DrawerProfile />
          </div>

          {withLinks && <DrawerNavigation onNavigate={handleDrawerClose} />}
        </div>
      </Drawer>

      <About open={aboutOpen} close={() => setAboutOpen(false)} />
    </>
  );
};

export default Navigation;
